import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import CustomDialog from './CustomDialog';
import { useOtpTimer } from './useOtpTimer';
import './Auth.css';

const RED = '#c0392b';

const ErrMsg = ({ msg }) =>
  msg ? (
    <p style={{
      color: RED, fontSize: '12px', fontWeight: '600',
      margin: '4px 0 6px 0', padding: '4px 8px',
      background: '#fff0f0', borderLeft: `3px solid ${RED}`, borderRadius: '3px'
    }}>⚠ {msg}</p>
  ) : null;

const checkPassword = (val) => {
  if (!val) return 'Password is required';
  if (val.length < 8) return `Password must be at least 8 characters`;
  const specials = '!@#$%^&*()_+-=[]{};\':"|,.<>/?\\`~';
  let hasSpecial = false;
  for (let i = 0; i < val.length; i++) {
    if (specials.indexOf(val[i]) !== -1) { hasSpecial = true; break; }
  }
  if (!hasSpecial) return 'Password must include at least one special character (e.g. @, #, !, $)';
  return '';
};

function SignUp() {
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [countryCode, setCountryCode] = useState('+91');

  const [nameErr, setNameErr] = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [mobileErr, setMobileErr] = useState('');
  const [passwordErr, setPasswordErr] = useState('');

  const [dialog, setDialog] = useState(null);
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [otp, setOtp] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const { countdown, canResend, restart } = useOtpTimer(30);
  const navigate = useNavigate();

  // ── Live validators ───────────────────────────────────────────────────────
  const validateEmail = (v) => {
    if (!v.trim()) return 'Email is required';
    if (!v.includes('@')) return 'Email must contain @ symbol (e.g. name@example.com)';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Enter a valid email address';
    return '';
  };

  const validateMobile = (v) => {
    if (!v) return 'Mobile number is required';
    if (v.length < 10) return `Mobile must be exactly 10 digits (entered: ${v.length})`;
    return '';
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const ne = name.trim() ? '' : 'Name is required';
    const ee = validateEmail(email);
    const me = validateMobile(mobile);
    const pe = checkPassword(password);

    setNameErr(ne);
    setEmailErr(ee);
    setMobileErr(me);
    setPasswordErr(pe);

    if (ne || ee || me || pe) return; // block — do NOT call API

    try {
      const fullMobile = countryCode + mobile;
      await authAPI.register({ name, email, password, mobile: fullMobile, companyName });
      setDialog({
        message: 'OTP sent to your email and mobile. Please verify to complete registration.',
        type: 'success',
        onClose: () => { setDialog(null); setShowOtpForm(true); }
      });
    } catch (error) {
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('email') && msg.includes('already')) {
        setEmailErr('This email is already registered. Please login or use a different email.');
      } else {
        setDialog({ message: error.message || 'Something went wrong', type: 'error', onClose: () => setDialog(null) });
      }
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    try {
      await authAPI.verifyOtp({ email, otp });
      setDialog({
        message: 'Registration completed successfully! Please login.',
        type: 'success',
        onClose: () => { setDialog(null); navigate('/login'); }
      });
    } catch (error) {
      setDialog({ message: error.message || 'Invalid OTP', type: 'error', onClose: () => setDialog(null) });
    }
  };

  const handleResendOtp = async () => {
    setResending(true); setResendMsg('');
    try {
      await authAPI.resendOtp(email);
      setResendMsg('OTP resent successfully'); restart();
    } catch (err) {
      setResendMsg(err.message || 'Failed to resend OTP');
    } finally { setResending(false); }
  };

  // ── OTP screen ────────────────────────────────────────────────────────────
  if (showOtpForm) {
    return (
      <>
        <div className="auth-container" onClick={() => navigate('/')} style={{ alignItems: 'flex-start', overflowY: 'auto' }}>
          <div className="otp-card" onClick={(e) => e.stopPropagation()} style={{ marginTop: '40px', marginBottom: '40px' }}>
            <div className="otp-check-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="#107FDF"/>
                <path d="M7 12.5l3.5 3.5 6.5-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="otp-sent-text">OTP sent to your <strong>email</strong></p>
            <form onSubmit={handleOtpSubmit} className="otp-form">
              <input
                type="text" placeholder="Enter 6-digit OTP"
                value={otp} onChange={(e) => setOtp(e.target.value)}
                maxLength={6} required className="otp-input"
              />
              <button type="submit" className="otp-submit-btn">Submit</button>
              <div style={{ textAlign: 'center', marginTop: '4px' }}>
                {canResend ? (
                  <button type="button" onClick={handleResendOtp} disabled={resending}
                    style={{ background: 'none', border: 'none', color: '#107FDF', fontWeight: '600', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}>
                    {resending ? 'Resending...' : 'Resend OTP'}
                  </button>
                ) : (
                  <span style={{ fontSize: '13px', color: '#888' }}>Resend OTP in {countdown}s</span>
                )}
                {resendMsg && (
                  <p style={{ fontSize: '12px', marginTop: '4px', color: resendMsg.includes('success') ? '#16a34a' : '#dc2626' }}>{resendMsg}</p>
                )}
              </div>
            </form>
            <div className="otp-back">
              <a href="/login" style={{ color: '#107FDF', fontWeight: '600', textDecoration: 'none', fontSize: '13px' }}>Back to Login</a>
            </div>
          </div>
        </div>
        {dialog && <CustomDialog message={dialog.message} type={dialog.type} onClose={dialog.onClose} />}
      </>
    );
  }

  // ── Sign Up form ──────────────────────────────────────────────────────────
  return (
    <>
      <div className="auth-container">
        <div className="auth-card" onClick={(e) => e.stopPropagation()}>
          <button className="auth-back-btn" onClick={() => navigate('/')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            Back to Home
          </button>

          <div className="auth-header">
            <img src="/images/corgif-logo.png" alt="CorGif Logo" className="auth-logo" />
            <h2>Create Account</h2>
            <p>Sign up to get started</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form" noValidate>

            {/* Name */}
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text" placeholder="Enter your name" value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ borderColor: nameErr ? RED : '' }}
              />
              <ErrMsg msg={nameErr} />
            </div>

            {/* Company */}
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text" placeholder="Enter your company name" value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>

            {/* Email */}
            <div className="form-group">
              <label>Email *</label>
              <input
                type="text" placeholder="Enter your work email" value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailErr(''); }}
                style={{ borderColor: emailErr ? RED : '' }}
              />
              <ErrMsg msg={emailErr} />
            </div>

            {/* Mobile */}
            <div className="form-group">
              <label>Mobile Number *</label>
              <div className="mobile-input-row">
                <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="country-code-select">
                  <option value="+91">+91 (India)</option>
                  <option value="+1">+1 (USA)</option>
                  <option value="+44">+44 (UK)</option>
                  <option value="+61">+61 (Australia)</option>
                  <option value="+971">+971 (UAE)</option>
                  <option value="+65">+65 (Singapore)</option>
                  <option value="+86">+86 (China)</option>
                  <option value="+81">+81 (Japan)</option>
                  <option value="+82">+82 (S. Korea)</option>
                  <option value="+49">+49 (Germany)</option>
                  <option value="+33">+33 (France)</option>
                  <option value="+39">+39 (Italy)</option>
                  <option value="+34">+34 (Spain)</option>
                  <option value="+7">+7 (Russia)</option>
                  <option value="+55">+55 (Brazil)</option>
                  <option value="+27">+27 (S. Africa)</option>
                </select>
                <input
                  type="tel" placeholder="10-digit mobile number" value={mobile}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setMobile(v);
                    setMobileErr('');
                  }}
                  maxLength="10"
                  className="mobile-number-input"
                  style={{ borderColor: mobileErr ? RED : '' }}
                />
              </div>
              <ErrMsg msg={mobileErr} />
            </div>

            {/* Password */}
            <div className="form-group">
              <label>Password *</label>
              <input
                type="password" placeholder="Min 8 chars + special character" value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordErr(''); }}
                style={{ borderColor: passwordErr ? RED : '' }}
              />
              <ErrMsg msg={passwordErr} />
            </div>

            <button type="submit" className="auth-submit-btn">Sign Up</button>
          </form>

          <div className="auth-footer">
            <p>Already have an account? <a href="/login">Login</a></p>
          </div>
        </div>
      </div>
      {dialog && <CustomDialog message={dialog.message} type={dialog.type} onClose={dialog.onClose} />}
    </>
  );
}

export default SignUp;
